// src/lib/i18n/languages.ts
// ─────────────────────────────────────────────────────────────────────────
// Sprint: Frontend UI i18n Foundation V1, Parte A.
//
// Deliberately re-exports `Language` from usePreferences.ts rather than
// declaring a second, possibly-drifting union — that hook (Hotfix/Sprint:
// Language Selector Marketplace Wiring V1) is already the single source of
// truth for "es"|"en"|"pt" (ALLOWED_LANGUAGES, validated at load/set time)
// and for Language -> market_locale (languageToMarketLocale()). This
// module adds only what usePreferences.ts doesn't already own: which
// language the UI copy layer falls back to, and a runtime guard for values
// that didn't come through usePreferences' own validation (e.g. a plain
// string from an untyped call site).
import type { Language } from "@/lib/hooks/usePreferences";

export type { Language };

export const SUPPORTED_LANGUAGES: readonly Language[] = ["es", "en", "pt"];

// Fallback language for both: an unrecognized/missing language value, and
// any message key missing from a non-Spanish table (see messages.ts — ES
// is the source-of-truth table every other language is typed against).
export const FALLBACK_LANGUAGE: Language = "es";

export function isSupportedLanguage(value: unknown): value is Language {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

// Never used for UI copy (see messages.ts's own "why not translate this"
// note) — only for the one non-copy spot that needs a locale for
// Intl/Date formatting (price disclaimer "updated on {date}" text).
export const INTL_LOCALE_BY_LANGUAGE: Record<Language, string> = {
  es: "es-CO",
  en: "en-US",
  pt: "pt-BR",
};
